import { getNameResourceFromString } from '@vendia/management-api-rbac'
import { ActionEnum, Capability } from '@vendia/management-api-types'
import { NameResourceRepresentation, RoleTemplate, VendiaResourceRepresentation } from 'src/types/types'
import Icon from 'src/components/icons/icon'

export const DEFAULT_ROLE_TEMPLATE_NAME = 'Default Access'
export const CUSTOM_ROLE_TEMPLATE_NAME = 'Custom (advanced)'

export function nameResourceFromEmail({ email }: { email: string }): NameResourceRepresentation {
  // Returns an empty NameResource to allow building permissions previews for inviting new members
  if (!email) return `NameResource()` as NameResourceRepresentation
  const parts = getNameResourceFromString(email)
  return `NameResource(${parts.name}@${parts.subdomain}.${parts.domain}.${parts.ext})`
}

export function getRoleTemplates({ orgId, orgDomains }: { orgId: string; orgDomains: string[] }): RoleTemplate[] {
  return [
    // We show the "Default" permissions so admins can see what it looks like, though
    // currently the source of truth for this is on the server.
    // TODO: Would be better to allow orgs to configure the default permission set and then return it with the getOrg call.
    {
      name: DEFAULT_ROLE_TEMPLATE_NAME,
      description: `Default permissions for new users. Can create and modify own projects, but can't see other members' projects.`,
      capabilities: getDefaultRoleCapabilities({ orgId, orgDomains }),
    },
    {
      name: 'Project Developer (read-only)',
      description: `In addition to the default permissions, gives the user the ability to view all projects and workspaces in the Org.`,
      capabilities: getUniReadOnlyCapabilities({ orgId, orgDomains }),
    },
    {
      name: 'Project Developer',
      description: `View all projects and workspaces, update project settings, perform schema evolution, and write data.`,
      capabilities: getUniDeveloperCapabilities({ orgId, orgDomains }),
    },
    {
      name: 'Project Admin',
      description: `Full permissions to administer all projects. Can view, update, and delete any project. Does not include user management.`,
      capabilities: getUniAdminCapabilities({ orgId, orgDomains }),
    },
    /*
    Not including User admin for now because current permission boundary enforcement code requires someone to essentially have
    UNI_ALL set to NameResource(*@*.customdomain.com) in order to be able to set NameResources for any UNI_ permissions. If they
    have that then they aren't that different from a Uni admin. Can revisit later.
    */
    // {
    //   name: 'User admin',
    //   description: `Full permissions to administer all users in the org. Can invite new users to the Org and update all users' permissions. Does not include any additional permissions for viewing/updating Projects.`,
    //   capabilities: getUserAdminCapabilities({ orgId, orgDomains, email }),
    // },
    {
      name: 'Org Admin',
      description: `Full permissions to administer all users and projects. Can invite users, update permissions, and manage all projects.`,
      capabilities: getOrgAdminCapabilities({ orgId, orgDomains }),
    },
    {
      name: 'Restricted Access',
      description: `An extremely limited set of permissions. Users retain read-only access to Projects they've created.`,
      capabilities: getRestrictedAccessCapabilities({ orgId }),
    },
    {
      name: CUSTOM_ROLE_TEMPLATE_NAME,
      // FIXME: link doesn't work in RadioOption but planning to refactor this setup...
      description: (
        <div className='text-neutral-8 text-xs'>
          Configure all of the specific capabilities for this role. For detailed information on the available actions
          and resource formats,{' '}
          <a
            href='https://docs.vendia.com/platform/operational/secure-data-sharing/rbac'
            target='_blank'
            rel='noopener noreferrer'
          >
            <span className='text-link inline-flex items-center gap-1'>
              <span>please reference the documentation.</span>
              <Icon size={'xxs'} name='external' />
            </span>
          </a>
        </div>
      ),
    },
  ]
}

function getRestrictedAccessCapabilities({ orgId }: any) {
  return [...getOrgCapabilities({ orgId, isRestricted: true }), ...getGetOwnedUnisOnly()]
}

function getDefaultRoleCapabilities({ orgId, orgDomains }: any) {
  return [
    ...getOrgCapabilities({ orgId, isAdmin: false }),
    ...getUniCreateJoin({ orgDomains }),
    ...getCommonCapabilities(),
  ]
}

function getUniDeveloperCapabilities({ orgId, orgDomains }: any) {
  const capabilities = getDefaultRoleCapabilities({ orgId, orgDomains })
  const actionsToUpgrade = ['UNI_GET', 'UNI_MUTATE', 'UNI_EVOLVE_SCHEMA']
  actionsToUpgrade.forEach((action) => {
    const capability = capabilities.find((c) => c.action === action)
    // @ts-ignore - capability could be undefined but we know it exists in this context
    capability.resources = [...capability.resources, ...getStarUniResourcesForOrgDomains({ orgDomains })]
  })
  return capabilities.concat(getDataAllCapabilities({ orgId }))
}

function getUniReadOnlyCapabilities({ orgId, orgDomains }: any) {
  const capabilities = getDefaultRoleCapabilities({ orgId, orgDomains })
  const uniActionsToUpgrade = ['UNI_GET']
  uniActionsToUpgrade.forEach((action) => {
    const capability = capabilities.find((c) => c.action === action)
    // @ts-ignore - capability could be undefined but we know it exists in this context
    capability.resources = [...capability.resources, ...getStarUniResourcesForOrgDomains({ orgDomains })]
  })
  return capabilities.concat(getDataReadOnlyCapabilities({ orgId }))
}

function getDataReadOnlyCapabilities({ orgId }: any): Capability[] {
  return [
    {
      action: ActionEnum.DataRead,
      resources: [getStarVendiaResourceForOrg({ orgId })],
      conditions: [],
    },
  ]
}

function getDataAllCapabilities({ orgId }: any): Capability[] {
  return [
    {
      action: ActionEnum.DataAll,
      resources: [getStarVendiaResourceForOrg({ orgId })],
      conditions: [],
    },
  ]
}

function getUniAdminCapabilities({ orgId, orgDomains }: any) {
  return [
    ...getOrgCapabilities({ orgId, isAdmin: false }),
    ...getAdminUniAllCapabilities({ orgDomains }),
    ...getUniCreateJoin({ orgDomains }),
    ...getCommonCapabilities(),
    ...getDataAllCapabilities({ orgId }),
  ]
}

function getUserAdminCapabilities({ orgId, orgDomains }: any) {
  return [
    ...getOrgCapabilities({ orgId, isAdmin: false }),
    ...getUserAllCapabilities({ orgDomains }),
    ...getUniCreateJoin({ orgDomains }),
    ...getCommonCapabilities(),
  ]
}

function getOrgAdminCapabilities({ orgId, orgDomains }: any) {
  return [
    ...getOrgCapabilities({ orgId, isAdmin: true }),
    ...getOrgAdminRoleCapabilities({ orgId }),
    ...getUserAllCapabilities({ orgDomains }),
    ...getAdminUniAllCapabilities({ orgDomains }),
    ...getUniCreateJoin({ orgDomains }),
    ...getCommonCapabilities(),
    ...getDataAllCapabilities({ orgId }),
  ]
}

function getOrgCapabilities({ orgId, isAdmin = false, isRestricted = false }: any): Capability[] {
  if (isRestricted) {
    return [
      {
        action: ActionEnum.OrgGet,
        resources: [`OrganizationResource(${orgId})`],
        conditions: [],
      },
    ]
  }
  if (isAdmin) {
    return [
      {
        action: ActionEnum.OrgAll,
        resources: [`OrganizationResource(${orgId})`],
        conditions: [],
      },
    ]
  }
  return [
    {
      action: ActionEnum.OrgGet,
      resources: [`OrganizationResource(${orgId})`],
      conditions: [],
    },
    {
      action: ActionEnum.OrgListUsers,
      resources: [`OrganizationResource(${orgId})`],
      conditions: [],
    },
  ]
}

function getOrgAdminRoleCapabilities({ orgId }: any): Capability[] {
  return [
    {
      action: ActionEnum.RoleAll,
      resources: [`OrganizationResource(${orgId})`],
      conditions: [],
    },
  ]
}

function getStarUniResourcesForOrgDomains({ orgDomains }: any) {
  return orgDomains.map((d: any) => `UniResource(*.*.${d}#*)`)
}

function getStarNameResourcesForOrgDomains({ orgDomains }: any) {
  return orgDomains.map((d: any) => `NameResource(*@*.${d})`)
}

function getStarVendiaResourceForOrg({ orgId }: any): VendiaResourceRepresentation {
  return `VendiaResource(${orgId}/*)`
}

function getAdminUniAllCapabilities({ orgDomains }: any): Capability[] {
  return [
    {
      action: ActionEnum.UniAll,
      resources: [
        // Needed to be able to set user's UNI_ permissions to UniResource(*.*.customdomain.com#*)
        ...getStarUniResourcesForOrgDomains({ orgDomains }),
        // Needed to be able to set user's UNI_ permissions to NameResource(someuser@*.customdomain.com) without exceeding permission boundary
        // Typically used to give user's the ability to mutate their own Unis, eg UNI_RESET set to NameResource(me@*.customdomain.com)
        ...getStarNameResourcesForOrgDomains({ orgDomains }),
      ],
      conditions: [],
    },
  ]
}

function getUniCreateJoin({ orgDomains }: any): Capability[] {
  return [
    {
      action: ActionEnum.UniJoin,
      resources: getStarUniResourcesForOrgDomains({ orgDomains }),
      conditions: [],
    },
    {
      action: ActionEnum.UniCreate,
      resources: getStarUniResourcesForOrgDomains({ orgDomains }),
      conditions: [],
    },
  ]
}

function getUserAllCapabilities({ orgDomains }: any): Capability[] {
  return [
    {
      action: ActionEnum.UserAll,
      resources: getStarNameResourcesForOrgDomains({ orgDomains }),
      conditions: [],
    },
  ]
}

function getGetOwnedUnisOnly(): Capability[] {
  return [
    {
      action: ActionEnum.UniGet,
      resources: ['VendiaResource(owned)'],
      conditions: [],
    },
  ]
}

function getCommonCapabilities(): Capability[] {
  const ownedResources = 'VendiaResource(owned)'
  return [
    {
      action: ActionEnum.UniGet,
      resources: [ownedResources],
      conditions: [],
    },
    {
      action: ActionEnum.UniMutate,
      resources: [ownedResources],
      conditions: [],
    },
    {
      action: ActionEnum.UniEvolveSchema,
      resources: [ownedResources],
      conditions: [],
    },
    {
      action: ActionEnum.UniInvite,
      resources: [ownedResources],
      conditions: [],
    },
    {
      action: ActionEnum.UniReset,
      resources: [ownedResources],
      conditions: [],
    },
    {
      action: ActionEnum.UniDelete,
      resources: [ownedResources],
      conditions: [],
    },
    {
      action: ActionEnum.UniDeleteNode,
      resources: [ownedResources],
      conditions: [],
    },
    {
      action: ActionEnum.UserInvite,
      resources: ['NameResource(*@*.*.*)'],
      conditions: [],
    },
  ]
}
